import { Component, ComponentInterface, Element, h, Host, Listen, Prop, State } from '@stencil/core';
import { addEventListener, removeEventListener } from '../../utils/helpers';

interface IMediaElement {
  src: string;
  caption?: string;
  copyright?: string;
  nodeguid?: string;
  download?: string;
  type: 'image' | 'video' | 'iframe';
}

@Component({
  tag: 'vds-lightbox',
  styleUrl: 'vds-lightbox.scss',
  shadow: false,
})
export class VdsLightbox implements ComponentInterface {

  private desktopMediaQuery = `(min-width: ${getComputedStyle(document.documentElement).getPropertyValue('--vt-breakpoint-md').trim()})`;
  private anchorElements: HTMLAnchorElement[] = [];
  private modalElement?: HTMLVdsModalElement;
  private contentSlides?: HTMLVdsSlidesElement;
  private mediaSlides?: HTMLVdsSlidesElement;
  private videoElements: HTMLVideoElement[] = [];
  private iframeElements: HTMLIFrameElement[] = [];

  /**
   * Gets access to the host element within the class instance
   */
  @Element() el!: HTMLElement;

  /**
   * Sets the open state of the lightbox modal
   */
  @Prop({ mutable: true, reflect: true }) open = false;

  /**
   * Label of the download link
   */
  @Prop() downloadLabel?: string;

  /**
   * Label of the press-binder link
   */
  @Prop() pressBinderLabel?: string;

  /**
   * Path to Lottie Animation for Press-Binder
   */
  @Prop() pressBinderAnimation: string;

  /**
   * The element within the scope of this component where
   * anchor elements are searched
   */
  @Prop() target: string = ":scope a:not([target=_blank]):not([download])";

  /**
   * Sets the visibility of press-binder buttons
   */
  @Prop() showPressBinder: boolean = false;

  /**
   * Sets visibility of download button
   */
  @Prop() showDownload: boolean = false;

  /**
   * Desktop Media-Query State
   * is true when viewport matches desktop media-query
   */
  @State() isDesktop = window.matchMedia(this.desktopMediaQuery).matches;

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.isDesktop = window.matchMedia(this.desktopMediaQuery).matches;
  }

  render() {
    return (
      <Host>
        <slot></slot>
        { this.renderModal() }
      </Host>
    );
  }

  async connectedCallback(): Promise<void> {
    const anchorElements = await this.getAnchorElements();
    anchorElements.forEach(x => addEventListener(x, 'click', (e) => this.handleAnchorElementClick(e, x)));
  }

  disconnectedCallback(): void {
    this.anchorElements.forEach(x => removeEventListener(x, 'click', (e) => this.handleAnchorElementClick(e, x)));
  }

  async componentDidRender(): Promise<void> {
    this.setupUIKitLightbox();

    if (this.mediaSlides && this.contentSlides) {
      await this.contentSlides.lockSwipes(true);
      addEventListener(this.mediaSlides, 'vdsSlideDidChange', () => this.onSlideDidChange(this.mediaSlides));
    }
  }

  componentDidUpdate(): void {
    if (!this.isDesktop) {
      this.modalElement.closeModal();
    }
  }

  private getAnchorElements(): Promise<HTMLAnchorElement[]> {
    return new Promise((resolve) => {
      const anchorElements = this.anchorElements = Array.from(this.el.querySelectorAll(this.target));
      resolve(anchorElements);
    });
  }

  private async handleAnchorElementClick(event: Event, element: HTMLAnchorElement): Promise<void> {
    if (this.isDesktop) {
      event.preventDefault();
      const index = this.anchorElements.findIndex(e => e == element);
      const activeIndex = index > -1 ? index : 0;
      await this.mediaSlides.slideTo(activeIndex, 0);
      await this.contentSlides.slideTo(activeIndex, 0);
      await this.modalElement.openModal();
    }
  }

  private handleDownloadAnchorElementClick(element: IMediaElement): void {
    const fakeAnchor = document.createElement('a');
    fakeAnchor.href = element.download;
    fakeAnchor.target = '_blank';
    fakeAnchor.click();
  }

  private getContentType(source: string): 'image' | 'video' | 'iframe' {
    const imageFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'tiff', 'bmp', 'ico'];
    const videoFileTypes = ['mov', 'mp4', 'avi', 'flv', 'mkv', 'wmv'];
    if (imageFileTypes.some(x => source.endsWith(x))) return 'image';
    else if (videoFileTypes.some(x => source.endsWith(x)) || source.match("canto.de")) return 'video';
    else return 'iframe';
  }

  private createMediaElements(elements: HTMLAnchorElement[]): IMediaElement[] {
    return elements.map((x): IMediaElement => {
      return {
        src: x.href,
        caption: x.dataset.caption,
        copyright: x.dataset.copyright,
        nodeguid: x.dataset.nodeguid,
        download: x.dataset.download,
        type: this.getContentType(x.href)
      }
    });
  }

  private renderModal(): string {
    const mediaElements = this.createMediaElements(this.anchorElements);

    return (
      <vds-modal ref={(el) => (this.modalElement = el)}
                 onVdsModalShown={() => this.open = true}
                 onVdsModalHidden={() => this.closeLightbox()}
                 lightbox
                 footer>

        <div slot="body" class="overflow-x-clip w-full rounded-[5px]">
          <vds-slides ref={(el) => (this.mediaSlides = el)} navigation={mediaElements.length > 1} options={{ spaceBetween: 20, rewind: true, effect: 'slide'}}>
            { mediaElements.map((element) =>
              <vds-slide>
                { element.type === "image" && this.renderImage(element) }
                { element.type == "video" &&  this.renderVideo(element) }
                { element.type == "iframe" &&  this.renderIframe(element) }
              </vds-slide>
            )}
          </vds-slides>
        </div>

        <div slot="footer" class="overflow-hidden">
          <vds-slides ref={(el) => (this.contentSlides = el)} options={{allowTouchMove: false, effect: 'fade', rewind: true}}>
            { mediaElements.map((element) => this.renderFooterElement(element))}
          </vds-slides>
        </div>

      </vds-modal>
    )
  }

  private async onSlideDidChange(slides: HTMLVdsSlidesElement): Promise<void> {
    slides.stopVideos();
    const index = await slides.getActiveIndex();
    await this.contentSlides.slideTo(index);
  }

  private setupUIKitLightbox(): void {
    const UIkit: any = window['UIkit'];
    if (UIkit) {
      if (this.isDesktop) {
        UIkit.lightbox(this.el).$destroy();
      }
      else {
        UIkit.lightbox(this.el);
      }
    }
  }

  private renderFooterElement(element: IMediaElement): string {
    return (
      <vds-slide>
        <div class="grid grid-cols-2 gap-10 w-full h-full bg-white">
          <div class="col-span-1 text-grey-light text-sm">
            <span class="block">{element.caption}</span>
            <span class="block">{element.copyright}</span>
          </div>
          <div class="col-span-1 flex justify-end">
            { (this.showDownload && element.download) && this.renderDownloadLink(element) }
            { (this.showPressBinder && element.nodeguid) && this.renderAddToPressBinderLink(element) }
          </div>
        </div>
      </vds-slide>
    );
  }

  private renderDownloadLink(element: IMediaElement): string {
    return (
      <div class="flex mr-5 items-start">
        <vds-icon-link
          href={element.download}
          label={this.downloadLabel}
          icon-color="berry"
          font-size="18px"
          lineHeight="30px"
          label-color="grey"
          tablet-growth="false"
          download={true}
          last={true}
          onClick={() => this.handleDownloadAnchorElementClick(element)}>
          <svg slot="icon-start" xmlns="http://www.w3.org/2000/svg" data-name="2px_Line_Icons" viewBox="44.05 44 39.95 40" width="28">
            <path d="M83,70v5c0,2.25-.3,4.22-2,6s-3.7,2-5.95,2c-2,0-20.05,0-22,0-2.25,0-4.26-.31-6-2s-2-3.71-2-6V70" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2px"></path>
            <path d="M73,66l-7,7a2.54,2.54,0,0,1-4,0l-7-7" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2px"></path>
            <path d="M64 67 64 45" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2px"></path>
          </svg>
        </vds-icon-link>
      </div>
    )
  }

  private renderAddToPressBinderLink(element: IMediaElement): string {
    return (
      <div class="flex items-start">
        <vds-add-to-press-binder class="-mt-1"
                                 nodeguid={element.nodeguid}
                                 label={this.pressBinderLabel}
                                 animation={this.pressBinderAnimation}></vds-add-to-press-binder>
      </div>
    )
  }

  private renderImage(element: IMediaElement): string {
    return (
      <div class="w-full aspect-video">
        <img class="rounded object-contain h-full" src={element.src} width="1920" height="1080" alt={element.caption} />
      </div>
    )
  }

  private renderVideo(element: IMediaElement): string {
    return (
      <video ref={(nativeElement) => this.videoElements.push(nativeElement)}
             class="w-full rounded aspect-video"
             width="1920"
             height="1080"
             preload="metadata"
             controls>
            <source src={element.src} type="video/mp4" />
        </video>
    )
  }

  private renderIframe(element: IMediaElement): string {
    return (
      <iframe ref={(nativeElement) => this.iframeElements.push(nativeElement)}
              class="w-full aspect-video rounded"
              src={element.src}
              title={element.caption}
              frameborder="0"
              allowFullScreen></iframe>
    )
  }

  private closeLightbox(): void {
    this.mediaSlides.stopVideos();
    this.open = false;
  }

}
